// Visitor & Contractor Management (VCM) entitlement — RON-5460 / RON-5466.
//
// The module is sold per company. Three values on `tblcompany_property` gate it:
//   visitor_portal    TINYINT(1)  whole-module gate
//   contractor_portal TINYINT(1)  contractor add-on (register, vetting, compliance, invites)
//   max_sites         SMALLINT    how many sites the company may create (default 1)
//
// This file deliberately mirrors the `HasEmergencyRoles` pattern in `SecurityService.js`:
// the flags live in localStorage, are refreshed from an API payload, and broadcast a
// `window` CustomEvent so the Sidebar re-renders without prop drilling.
//
// TWO IMPORTANT DIFFERENCES FROM HasEmergencyRoles
//
// 1. DEFAULT IS OFF. `HasEmergencyRoles` defaults to 1 (visible) when the key is absent.
//    Visitor entitlement must default to 0 (hidden), because `visitor_portal` DEFAULT 0 in
//    the schema and showing a paid module to a company that has not bought it is worse than
//    briefly hiding it from one that has.
//
// 2. CONTRACTOR REQUIRES VISITOR. Per the epic's hard rule there is no contractor-only
//    company, so `contractorPortal` is forced false whenever `visitorPortal` is false —
//    enforced in the getter, not just at write time, so a bad payload cannot leak a
//    contractor-only state into the UI.
//
// NOT WIRED YET: nothing calls `syncVisitorEntitlementFromPayload` at the moment. The agreed
// carrier is `HomeScreenBookings_api` (see the note in `useVisitorAccess.js` about coverage).

const VISITOR_PORTAL_KEY = "visitor_portal";
const CONTRACTOR_PORTAL_KEY = "contractor_portal";
const MAX_SITES_KEY = "max_sites";

export const VISITOR_ENTITLEMENT_EVENT = "visitorEntitlementChanged";

const DEFAULT_MAX_SITES = 1;

// A stored flag counts as ON only when it is exactly "1". Anything else — absent, "0",
// or the stringified-nullish values localStorage ends up with ("null" / "undefined") —
// is OFF. Same defensiveness as `SecurityService.isValidStoredValue`.
const readFlag = (key) => window.localStorage.getItem(key) === "1";

const toFlagString = (value) => String(Number(value) === 1 ? 1 : 0);

export function getVisitorEntitlement() {
  const visitorPortal = readFlag(VISITOR_PORTAL_KEY);
  const storedMaxSites = Number(window.localStorage.getItem(MAX_SITES_KEY));

  return {
    visitorPortal,
    // Hard rule: contractor add-on is only meaningful when the module itself is on.
    contractorPortal: visitorPortal && readFlag(CONTRACTOR_PORTAL_KEY),
    maxSites:
      Number.isFinite(storedMaxSites) && storedMaxSites > 0
        ? storedMaxSites
        : DEFAULT_MAX_SITES,
  };
}

export function setVisitorEntitlement({
  visitorPortal,
  contractorPortal,
  maxSites,
} = {}) {
  window.localStorage.setItem(VISITOR_PORTAL_KEY, toFlagString(visitorPortal));
  window.localStorage.setItem(CONTRACTOR_PORTAL_KEY, toFlagString(contractorPortal));

  const normalizedMaxSites = Number(maxSites);
  if (Number.isFinite(normalizedMaxSites) && normalizedMaxSites > 0) {
    window.localStorage.setItem(MAX_SITES_KEY, String(normalizedMaxSites));
  }

  window.dispatchEvent(
    new CustomEvent(VISITOR_ENTITLEMENT_EVENT, { detail: getVisitorEntitlement() })
  );
}

export function clearVisitorEntitlement() {
  window.localStorage.removeItem(VISITOR_PORTAL_KEY);
  window.localStorage.removeItem(CONTRACTOR_PORTAL_KEY);
  window.localStorage.removeItem(MAX_SITES_KEY);

  window.dispatchEvent(
    new CustomEvent(VISITOR_ENTITLEMENT_EVENT, { detail: getVisitorEntitlement() })
  );
}

// Reads the three flags out of an API payload and persists them.
//
// Absent flags are left untouched rather than written as 0, so an endpoint that simply does
// not carry them can never switch the module off for an entitled company. Returns the
// resulting entitlement so callers can act on it directly.
export function syncVisitorEntitlementFromPayload(payload) {
  const records =
    payload?.Data?.Records ?? payload?.data?.Records ?? payload?.data?.records;

  if (!records) {
    return getVisitorEntitlement();
  }

  const hasVisitorFlag =
    records.visitor_portal !== undefined && records.visitor_portal !== null;

  if (!hasVisitorFlag) {
    return getVisitorEntitlement();
  }

  setVisitorEntitlement({
    visitorPortal: records.visitor_portal,
    contractorPortal: records.contractor_portal,
    maxSites: records.max_sites,
  });

  return getVisitorEntitlement();
}
